import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Zap, TrendingUp, Database, Star } from 'lucide-react';
import type { NewSizingSnapshot } from '@/sizing/newSizingEngine';
interface BottleneckSummaryCardProps {
  snapshot: NewSizingSnapshot | null;
}
export const BottleneckSummaryCard = ({
  snapshot
}: BottleneckSummaryCardProps) => {
  if (!snapshot) {
    return <Card>
        <CardHeader>
          <CardTitle className="text-base">Constraint Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="h-16 animate-pulse rounded bg-muted" />
            <div className="h-16 animate-pulse rounded bg-muted" />
            <div className="h-16 animate-pulse rounded bg-muted" />
          </div>
        </CardContent>
      </Card>;
  }
  const bottleneck = snapshot.finalBottleneck;
  const maxGpus = Math.max(snapshot.promptGpus, snapshot.decodeGpus, snapshot.kvGpusP95);
  const constraints = [{
    id: 'prompt',
    label: 'Prompt Throughput',
    icon: Zap,
    gpus: snapshot.promptGpus,
    isBottleneck: bottleneck === 'prompt'
  }, {
    id: 'decode',
    label: 'Decode Throughput',
    icon: TrendingUp,
    gpus: snapshot.decodeGpus,
    isBottleneck: bottleneck === 'decode'
  }, {
    id: 'kv',
    label: 'KV Memory',
    icon: Database,
    gpus: snapshot.kvGpusP95,
    isBottleneck: bottleneck === 'kv'
  }];
  return <Card className="shadow-md">
      <CardHeader>
        <CardTitle className="text-base">Constraint Analysis</CardTitle>
        <p className="text-sm text-muted-foreground">GPU requirements by resource type</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {constraints.map(constraint => {
        const Icon = constraint.icon;
        const percentage = maxGpus > 0 ? constraint.gpus / maxGpus * 100 : 0;
        return <div key={constraint.id} className={`space-y-2 rounded-lg border p-3 ${constraint.isBottleneck ? 'border-amber-500 bg-amber-50/50 dark:bg-amber-950/20' : 'border-border'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${constraint.isBottleneck ? 'text-amber-600' : 'text-muted-foreground'}`} />
                  <span className="text-sm font-medium">{constraint.label}</span>
                  {constraint.isBottleneck && <Badge variant="default" className="ml-1 h-5 px-1.5 text-xs">
                      <Star className="h-3 w-3" />
                    </Badge>}
                </div>
                <span className="text-lg font-bold">{constraint.gpus}</span>
              </div>
              
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div className={`h-full rounded-full transition-all ${constraint.isBottleneck ? 'bg-amber-500' : 'bg-primary'}`} style={{
              width: `${percentage}%`
            }} />
              </div>
            </div>;
      })}

        <div className="mt-4 space-y-2 rounded-lg bg-primary/5 p-3 text-xs">
          <p className="text-muted-foreground">
            <span className="font-semibold text-foreground">
              {bottleneck === 'kv' ? 'KV Memory' : bottleneck === 'prompt' ? 'Prompt Throughput' : 'Decode Throughput'}
            </span>{' '}
            is the limiting constraint, driving the final GPU count to {snapshot.finalGpuCount}.
          </p>
          <p className="font-medium text-primary">
            {bottleneck === 'kv' ? '💡 To support more users: Reduce chat memory turns or add more servers' : '💡 To increase capacity: Increase batch size or add more GPUs'}
          </p>
        </div>
      </CardContent>
    </Card>;
};